import type React from "react";
import type { Request } from "@/types/research.types";

export function resizeTextarea(textarea: HTMLTextAreaElement | null, maxHeight = 120): void {
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
}

export function resetTextareaHeight(textarea: HTMLTextAreaElement | null): void {
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = "20px";
}

export function parseTriggerMatch(val: string): { menu: "slash" | "hash"; query: string } | null {
  const match = val.match(/(?:^|\s)([/#])([^ #/]*)$/);
  if (!match) return null;
  return {
    menu: match[1] === "/" ? "slash" : "hash",
    query: match[2] ?? "",
  };
}

export function handleCommandSelectLogic(
  commandContent: string,
  activeMenu: "slash" | "hash" | null,
  searchQuery: string,
  chatMessage: string,
  onSendMessage: (msg: string) => Promise<void> | void,
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  setActiveMenu: React.Dispatch<React.SetStateAction<"slash" | "hash" | null>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  request?: Request,
  onRequestSelect?: (request: Request) => void
): void {
  if (activeMenu === "slash") {
    onSendMessage(commandContent.trim());
    setChatMessage("");
    setActiveMenu(null);
    setSearchQuery("");
    resetTextareaHeight(textareaRef.current);
    return;
  }
  // For hash commands: if the request has chain_with, show confirmation dialog
  // via onRequestSelect (which sets pendingChainRequest state in the parent).
  if (request?.chain_with && request.chain_with.length > 0 && onRequestSelect) {
    setActiveMenu(null);
    setSearchQuery("");
    onRequestSelect(request);
    return;
  }
  const trigger = "#";
  const suffix = `${trigger}${searchQuery}`;
  const newVal = chatMessage.endsWith(suffix)
    ? chatMessage.slice(0, -suffix.length) + commandContent
    : chatMessage + commandContent;
  setChatMessage(newVal + " ");
  setActiveMenu(null);
  setSearchQuery("");
  textareaRef.current?.focus();
  setTimeout(() => resizeTextarea(textareaRef.current), 0);
}

type MenuType = "slash" | "hash" | null;
type ChainProgressState = { current: number; total: number } | null;

export function createTextareaChangeHandler(
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  setActiveMenu: React.Dispatch<React.SetStateAction<MenuType>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>
) {
  return (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatMessage(event.target.value);
    const tr = parseTriggerMatch(event.target.value);
    if (tr) {
      setActiveMenu(tr.menu);
      setSearchQuery(tr.query);
      setSelectedIndex(0);
    } else {
      setActiveMenu(null);
      setSearchQuery("");
      setSelectedIndex(0);
    }
    resizeTextarea(event.target);
  };
}

export function createKeyPressHandler(
  activeMenu: MenuType,
  currentRequests: Request[],
  selectedIndex: number,
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>,
  setActiveMenu: React.Dispatch<React.SetStateAction<MenuType>>,
  handleCommandSelect: (c: string, request?: Request) => void,
  handleSendMessage: () => void,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) {
  return (e: React.KeyboardEvent) => {
    if (activeMenu) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((p) => Math.min(p + 1, currentRequests.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (currentRequests.length > 0 && currentRequests[selectedIndex])
          handleCommandSelect(
            currentRequests[selectedIndex].content,
            currentRequests[selectedIndex]
          );
      } else if (e.key === "Escape") {
        e.preventDefault();
        setActiveMenu(null);
        textareaRef.current?.focus();
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
}

export function createRequestSelectHandler(
  requestMap: Record<string, Request>,
  onSendMessage: (message: string) => Promise<void> | void,
  setChainProgress: React.Dispatch<React.SetStateAction<ChainProgressState>>,
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) {
  return async (content: string, request?: Request) => {
    if (request) {
      await handleRequestChainExecution(
        request,
        requestMap,
        onSendMessage,
        setChainProgress,
        setChatMessage,
        textareaRef
      );
    } else {
      setChatMessage(content.replace(/\s+/g, " ").trim());
      resizeTextarea(textareaRef.current);
    }
  };
}

export async function handleRequestChainExecution(
  request: Request,
  requestMap: Record<string, Request>,
  onSendMessage: (message: string) => Promise<void> | void,
  setChainProgress: React.Dispatch<React.SetStateAction<{ current: number; total: number } | null>>,
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
): Promise<void> {
  if (request.chain_with != null && request.chain_with.length > 0) {
    const fullChain = [
      request.content,
      ...request.chain_with
        .map((id) => requestMap[id]?.content)
        .filter((c): c is string => c != null),
    ];
    try {
      for (let i = 0; i < fullChain.length; i++) {
        // Set progress before each send so the indicator starts at 1/N, not 0/N
        setChainProgress({ current: i + 1, total: fullChain.length });
        const msg = fullChain[i];
        if (msg != null) {
          await onSendMessage(msg.replace(/\s+/g, " ").trim());
        }
      }
    } finally {
      // Always clear progress, even if a send fails mid-chain
      setTimeout(() => setChainProgress(null), 2000);
    }
  } else {
    setChatMessage(request.content.replace(/\s+/g, " ").trim());
    resizeTextarea(textareaRef.current);
  }
}

export function createSendMessageHandler(
  chatMessage: string,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  onSendMessage: (message: string) => Promise<void> | void
): () => void {
  return () => {
    if (chatMessage.trim()) {
      onSendMessage(chatMessage.trim());
      setChatMessage("");
      resetTextareaHeight(textareaRef.current);
    }
  };
}
